<script setup lang="ts">
import { watch, onUnmounted } from 'vue';
import { useStorage } from '@vueuse/core';
import { SalesforceFormTypes, PlatformProducts } from '@pkg/types/salesforceForm';
import { TurnstileStatusValue } from '@pkg/form-builder/types';
import TurnstileFormShell from '@pkg/form-builder/web/components/TurnstileFormShell.vue';
import {
  FormFieldText,
  FormFieldPhone,
  FormFieldSelect,
  FormFieldCheckboxes,
  FormFieldNote,
} from '@pkg/ui/components/ui/form/fields';
import UiButton from '@pkg/ui/components/ui/button/ButtonUi.vue';
import SalesforceHtmlForm from './SalesforceHtmlForm.vue';
import { useSalesforceForm, convertMarketingParamsToForm } from './useSalesforceForm';
import { validateTurnstile } from './validateTurnstile';
import type {
  SalesforceBlockerInputTexts,
  SalesforceFieldsTexts,
  Option,
  PlatformProductFields,
} from './types';

const props = defineProps<{
  productInterest: SalesforceFormTypes;
  fieldsTexts: SalesforceFieldsTexts;
  blockerInputTexts: SalesforceBlockerInputTexts;
  platformOptions: PlatformProductFields;
  submitButtonText?: string;
}>();

const {
  blockerTitle,
  blockerDescription,
  userBlockerOptionTitle,
  businessBlockerOptionTitle,
  blockerValidationMessage,
} = props?.blockerInputTexts;

const blockerInputOptions: Option[] = [
  { title: businessBlockerOptionTitle, value: 'true' },
  { title: userBlockerOptionTitle, value: 'user' },
];

const newsletterCheckbox = [{ title: props?.fieldsTexts?.optInNewsletter, value: 'true' }];
const websiteRequired = props?.fieldsTexts?.productInterest === SalesforceFormTypes.DATE_LABELLING;
const platformProductRequired = props?.productInterest === SalesforceFormTypes.PLATFORM;

const platformProductOptions = ref<Option[]>();
if (platformProductRequired) {
  platformProductOptions.value = [
    { title: props.platformOptions?.platformAI, value: PlatformProducts.PLATFORM_AI },
    { title: props.platformOptions?.platformExpiry, value: PlatformProducts.PLATFORM_EXPIRY },
    { title: props.platformOptions?.platformConsumer, value: 'false' },
  ];
  if (props.platformOptions?.platformDonationsBusiness) {
    platformProductOptions.value?.push({
      title: props.platformOptions?.platformDonationsBusiness,
      value: PlatformProducts.PLATFORM_DONATIONS_BUSINESS,
    });
  }
  if (props.platformOptions?.platformDonationsCharity) {
    platformProductOptions.value?.push({
      title: props.platformOptions?.platformDonationsCharity,
      value: PlatformProducts.PLATFORM_DONATIONS_CHARITY,
    });
  }
}

const SFform = ref<HTMLElement>();

const fields = [
  { name: 'firstName', component: FormFieldText, required: true },
  { name: 'lastName', component: FormFieldText, required: true },
  { name: 'email', component: FormFieldText, type: 'email', overrideName: 'emailAddress', required: true },
  { name: 'company', component: FormFieldText, overrideName: 'companyName', required: true },
  { name: 'phone', component: FormFieldPhone, overrideName: 'phoneNumber', required: true },
  { name: 'website', component: FormFieldText, overrideName: 'websiteName', required: websiteRequired },
  {
    name: 'optInNewsletter',
    component: FormFieldCheckboxes,
    options: newsletterCheckbox,
    id: 'optInNewsletter',
    required: false,
  },
  { name: 'noteText', component: FormFieldNote, id: 'noteText', required: true },
];

const route = useRoute();
const {
  public: { domain, salesforce },
} = useRuntimeConfig();

const { $i18n } = useNuxtApp();
const t = $i18n.t;

const FORM_SUBMITTED_QUERY = 'formSubmitted=true';

const returnPath = computed(() => {
  if (!route.query.formSubmitted && Object.entries(route.query).length > 0) {
    return `${domain}${route.fullPath}&${FORM_SUBMITTED_QUERY}`;
  } else if (!route.query.formSubmitted && Object.entries(route.query).length === 0) {
    return `${domain}${route.fullPath}?${FORM_SUBMITTED_QUERY}`;
  }
  return `${domain}${route.fullPath}`;
});

const isFormSubmitted = computed(
  () => route?.query?.formSubmitted || route?.hash?.includes(FORM_SUBMITTED_QUERY),
);

const htmlForm = ref<HTMLFormElement | null>(null);
const { formResult } =
  htmlForm &&
  useSalesforceForm(
    blockerValidationMessage,
    props?.platformOptions?.platformOptionsValidationMessage,
    props?.productInterest,
  );
const { locale } = useI18n();

const finalFormValues = computed(() =>
  convertMarketingParamsToForm(
    document,
    { ...formResult.values },
    locale.value,
    props?.productInterest,
  ),
);

const errorsLength = computed(() => Object.entries(formResult.errors.value)?.length);

const submitHiddenForm = () => {
  const submitHtmlButton = document.getElementById('htmlSubmitButton') as HTMLButtonElement;
  submitHtmlButton.click();
};

let amplitudeForm = ref();

watch(
  () => props?.fieldsTexts?.productInterest || props?.fieldsTexts?.platformOption,
  (newForm) => {
    amplitudeForm = useStorage('amplitudeForm', {});
    let formName = 'OTHER';
    if (props?.fieldsTexts?.productInterest) {
      formName = props?.fieldsTexts?.productInterest;
    } else if (props?.fieldsTexts?.platformOption) {
      formName = 'PLATFORM';
    }

    if (newForm) {
      amplitudeForm.value = {
        formName: formName.toUpperCase(),
        formId: 'SalesForce',
      };
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  amplitudeForm.value = null;
});

const checkTurnstile = async () => {
  if (!turnstileToken.value) {
    return false;
  }
  return await validateTurnstile(turnstileToken.value);
};

const validTurnstile = ref(false);

const isSubmitDisabled = computed(() => !validTurnstile.value);

const onClickSubmit = async (ev: Event) => {
  ev.preventDefault();

  await formResult.validate();
  const serverTurnstileOk = await checkTurnstile();

  if (errorsLength.value === 0 && validTurnstile.value && serverTurnstileOk) {
    submitHiddenForm();
  }
};

onMounted(() => {
  if (isFormSubmitted.value) {
    SFform.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
});

const turnstileToken = ref<string | null>(null);
const handleTurnstileStatus = (_status: TurnstileStatusValue, token: string | null) => {
  turnstileToken.value = token;
  if (_status === TurnstileStatusValue.VERIFIED) {
    validTurnstile.value = true;
  }
};

const fieldLabel = (fieldName: string) =>
  props?.fieldsTexts?.[`${fieldName}Label` as keyof SalesforceFieldsTexts] as string | undefined;
const fieldPlaceholder = (fieldName: string) =>
  props?.fieldsTexts?.[`${fieldName}Placeholder` as keyof SalesforceFieldsTexts] as string | undefined;
</script>

<template>
  <div id="salesforce-form" ref="SFform" class="scroll">
    <div id="salesforce-html-form" class="hidden">
      <SalesforceHtmlForm
        :submit-url="salesforce?.formUrl"
        :oid="salesforce?.formOid"
        :values="finalFormValues"
        :return-path="returnPath"
      />
    </div>

    <div v-if="isFormSubmitted">
      <p>{{ t('formConfirmation') }}</p>
    </div>
    <div v-if="!isFormSubmitted" id="visible-form">
      <form @submit="(ev) => onClickSubmit(ev)">
        <div class="mb-16">
          <FormFieldSelect
            name="blockerInput"
            :label="blockerTitle"
            :options="blockerInputOptions"
            :description="blockerDescription"
            :required="true"
          />
        </div>
        <div v-if="platformProductRequired" class="mb-16">
          <FormFieldSelect
            name="platformProduct"
            :label="platformOptions?.platformOptionsLabel"
            :options="platformProductOptions"
            :required="true"
          />
        </div>
        <div v-for="(field, index) in fields" :key="index" class="mb-16">
          <component
            :is="field.component"
            :id="field?.id || undefined"
            :name="field.overrideName || field.name"
            :placeholder="fieldPlaceholder(field.name) || ''"
            :options="field?.options || undefined"
            :type="field?.type || undefined"
            :required="field.required"
            :label="fieldLabel(field.name)"
            :note-text="props?.fieldsTexts?.noteText || ''"
          />
        </div>
        <TurnstileFormShell @status-change="handleTurnstileStatus" />

        <UiButton type="submit" :disabled="isSubmitDisabled" @click="onClickSubmit">
          {{ submitButtonText }}
        </UiButton>
      </form>
    </div>
  </div>
</template>

<style scoped>
.scroll {
  scroll-margin-top: 50vh;
}
</style>
